import { Box, Flex, SimpleGrid } from "@chakra-ui/react";
import { FillImage } from "components/images";
import { type Theme } from "contexts/color-theme";
import { type ImageProps } from "next/image";
import React, { useMemo } from "react";
import { ProgressTabs, type ProgressTabsProps } from "ui/tabs";
import { TextColumn } from "ui/text";

type ProductFeature = {
  title: string;
  description: string;
  /**
   * The static image to display for mobile views. Will be displayed contained/centered
   * in the ProgressTabs media section against a secondary background color.
   * Best to use PNGs with a transparent bg.
   */
  mobileImage: ImageProps;
};

type ProductFeaturesWithAnimationProps = {
  theme: Theme;
  /** The animation media to display in desktop views. */
  animation: JSX.Element;
  /**
   * The product features that are shown below the animation for desktop views and with
   * their corresponding images in a slideshow for mobile views.
   */
  features: ProductFeature[];
};

/**
 * For desktop views, renders the Lottie animation with features shown below in a column layout.
 * For mobile, renders the features as a slideshow with their static images using ProgressTabs.
 */
export function ProductFeaturesWithAnimation({
  theme,
  features,
  animation: animationElement,
}: ProductFeaturesWithAnimationProps) {
  const tabs: ProgressTabsProps["tabs"] = useMemo(() => {
    return features.map((f) => ({
      title: f.title,
      description: f.description,
      media: (
        <Box bg="brand.backgroundSecondary.light">
          <FillImage {...f.mobileImage} style={{ objectFit: "contain", objectPosition: "center" }} />
        </Box>
      ),
    }));
  }, [features]);

  return (
    <>
      {/** Desktop view */}
      <Flex flexDir="column" gap="bigger" display={{ base: "none", md: "flex" }}>
        <Box borderRadius="bigger" overflow="hidden">
          {animationElement}
        </Box>

        <SimpleGrid columns={3} gap="bigger">
          {features.map((f) => (
            <TextColumn key={f.title} theme={theme} title={f.title} description={f.description} />
          ))}
        </SimpleGrid>
      </Flex>

      {/** Mobile view */}
      <Box display={{ base: "block", md: "none" }}>
        <ProgressTabs theme={theme} tabs={tabs} />
      </Box>
    </>
  );
}
